import { useEffect, type MutableRefObject, type ReactNode } from 'react';
import { Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useGridNavigation } from './useGridNavigation';

export interface LinesGridColumn<L> {
  key: string;
  title: string;
  width?: number;
  align?: 'left' | 'right' | 'center';
  /** `colIndex` передаётся в navRow/navCol редактируемых полей (IntInput/MoneyInput). */
  render: (line: L, rowIndex: number, colIndex: number) => ReactNode;
}

export interface LinesGridProps<L> {
  lines: L[];
  columns: LinesGridColumn<L>[];
  getRowKey: (line: L) => string;
  onAddRow: () => void;
  onRemoveRow: (key: string) => void;
  readOnly?: boolean;
  rowClassName?: (line: L) => string | undefined;
  footer?: ReactNode;
  /** Позволяет вызывающему коду перевести фокус на конкретную ячейку (например, в «Коробок»
   *  сразу после выбора товара, §5). */
  gridApiRef?: MutableRefObject<{ focusCell: (row: number, col: number) => void } | null>;
  /** Крестик удаления строки — по умолчанию последней колонкой, `'start'` переносит его первой
   *  (документы: рядом с кнопкой «+ категория» слева от выбора товара). */
  removeButtonPosition?: 'start' | 'end';
}

/** §1: таблица строк документа — поля горизонтально, Tab/Enter по полям, Enter в последнем поле
 *  добавляет строку. Обычный `<table>`, а не antd Table — инпут в каждой ячейке и клавиатурная
 *  навигация так устроены заметно проще. */
export function LinesGrid<L>({
  lines,
  columns,
  getRowKey,
  onAddRow,
  onRemoveRow,
  readOnly,
  rowClassName,
  footer,
  gridApiRef,
  removeButtonPosition = 'end',
}: LinesGridProps<L>) {
  const { containerRef, onKeyDownCapture, focusPendingRow, focusRow } = useGridNavigation(onAddRow);

  useEffect(() => {
    focusPendingRow();
  }, [lines.length, focusPendingRow]);

  useEffect(() => {
    if (!gridApiRef) return;
    gridApiRef.current = {
      focusCell: (row, col) => {
        if (col === 0) {
          focusRow(row);
          return;
        }
        containerRef.current?.querySelector<HTMLElement>(`[data-nav-row="${row}"][data-nav-col="${col}"]`)?.focus();
      },
    };
    return () => {
      gridApiRef.current = null;
    };
  }, [gridApiRef, focusRow, containerRef]);

  return (
    <div ref={containerRef} onKeyDownCapture={onKeyDownCapture} style={{ overflowX: 'auto' }}>
      <table className="lines-grid">
        <thead>
          <tr>
            {readOnly || removeButtonPosition !== 'start' ? null : <th style={{ width: 28 }} />}
            {columns.map((col) => (
              <th key={col.key} style={{ width: col.width, textAlign: col.align ?? 'left' }}>
                {col.title}
              </th>
            ))}
            {readOnly || removeButtonPosition !== 'end' ? null : <th style={{ width: 28 }} />}
          </tr>
        </thead>
        <tbody>
          {lines.map((line, rowIndex) => {
            /* Вне порядка Tab: иначе фокус попадает на крестик рядом с полем, и следующий Enter
               или пробел удаляет только что введённую строку (§1). */
            const removeCell = readOnly ? null : (
              <td key="remove">
                <Button
                  type="text"
                  danger
                  size="small"
                  tabIndex={-1}
                  aria-label="Удалить строку"
                  icon={<CloseOutlined />}
                  onClick={() => onRemoveRow(getRowKey(line))}
                />
              </td>
            );
            return (
              <tr key={getRowKey(line)} className={rowClassName?.(line)}>
                {removeButtonPosition === 'start' ? removeCell : null}
                {columns.map((col, colIndex) => (
                  <td key={col.key} style={{ textAlign: col.align ?? 'left' }}>
                    {col.render(line, rowIndex, colIndex)}
                  </td>
                ))}
                {removeButtonPosition === 'end' ? removeCell : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footer}
    </div>
  );
}
